import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PersonnelPTO from '../components/PersonnelPTO';
import {
  User, Briefcase, Calendar, Heart, ClipboardList,
  CheckCircle, XCircle, Mail, Phone, MapPin, Shield,
  Eye, EyeOff, Lock, Plug, Upload, PenLine, Download,
} from 'lucide-react';
import MyGmailConnection from '../components/MyGmailConnection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyProfile() {
  const [person, setPerson] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [changingPassword, setChangingPassword] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadItemId, setUploadItemId] = useState(null);
  const [busyItem, setBusyItem] = useState(null);

  useEffect(() => {
    fetchMyProfile();
  }, []);

  const fetchMyProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (!userStr || !token) return;

      const user = JSON.parse(userStr);
      // Try to find linked personnel record
      let personnelId = user.personnel_id;

      if (!personnelId) {
        // Try to find by email match
        const res = await axios.get(`${API}/personnel?limit=10000`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allPersonnel = res.data?.personnel || res.data || [];
        const match = allPersonnel.find(p =>
          (p.email && p.email.toLowerCase() === user.email?.toLowerCase()) ||
          (p.work_email && p.work_email.toLowerCase() === user.email?.toLowerCase())
        );
        if (match) personnelId = match.id;
      }

      if (personnelId) {
        const [personRes, onboardingRes] = await Promise.all([
          axios.get(`${API}/personnel/${personnelId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/personnel/${personnelId}/onboarding?viewer=self`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        ]);
        setPerson(personRes.data);
        setOnboardingData(onboardingRes.data);
      }
    } catch (e) {
      console.error('Failed to load profile:', e);
      toast.error('Failed to load your profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr + 'T00:00:00').toLocaleDateString(); } catch { return dateStr; }
  };

  const refreshOnboarding = async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API}/personnel/${person.id}/onboarding?viewer=self`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setOnboardingData(res.data);
  };

  const handleUpdateOnboardingItem = async (itemId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/personnel/${person.id}/onboarding/items/${itemId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Item marked as ${status}`);
      await refreshOnboarding();
    } catch (e) { toast.error('Failed to update item'); }
  };

  const triggerUpload = (item) => {
    setUploadItemId(item.id);
    if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    const itemId = uploadItemId;
    if (!file || !itemId) return;
    setBusyItem(itemId);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      // Server-side matcher: credential items run analyze+confirm (locked to this
      // person, filling the required-credential slot); plain files just save.
      const res = await axios.post(
        `${API}/personnel/${person.id}/onboarding/items/${itemId}/upload`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(res.data?.matched_credential_type
        ? `Uploaded & matched to ${res.data.matched_credential_type}`
        : 'Uploaded');
      if (res.data?.activated) toast.success('Onboarding complete — your status is now Active');
      await refreshOnboarding();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusyItem(null);
      setUploadItemId(null);
    }
  };

  const handleSignItem = async (item) => {
    const itemId = typeof item === 'string' ? item : item?.id;
    // If a request already exists and it's the hire's turn, open the existing
    // signing URL directly — no create POST, so retries never duplicate.
    if (item && typeof item === 'object' && item.esign_signing_url) {
      window.open(item.esign_signing_url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (busyItem) return; // guard against double-submit
    setBusyItem(itemId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/personnel/${person.id}/onboarding/items/${itemId}/sign`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.signing_url) window.open(res.data.signing_url, '_blank', 'noopener,noreferrer');
      else toast.success('Signature request ready');
      await refreshOnboarding();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start signing');
    } finally {
      setBusyItem(null);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to change password';
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="max-w-5xl mx-auto space-y-6" data-testid="my-profile-page">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <User size={28} className="text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">My Account</h1>
            <p className="text-muted-foreground text-sm">Your account is not linked to a personnel record. Contact your administrator for full profile access.</p>
          </div>
        </div>
        <div className="flex gap-1 border-b">
          <button
            className="px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 border-primary text-primary"
            data-testid="my-profile-tab-security"
          >
            <Shield size={16} /> Security
          </button>
        </div>
        <Card className="p-6 max-w-lg" data-testid="my-security-settings">
          <h3 className="font-medium mb-6 flex items-center gap-2"><Lock size={16} /> Change Password</h3>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {[
              { key: 'current_password', label: 'Current Password', visKey: 'current' },
              { key: 'new_password', label: 'New Password', visKey: 'new' },
              { key: 'confirm_password', label: 'Confirm New Password', visKey: 'confirm' },
            ].map(({ key, label, visKey }) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={key}>{label}</Label>
                <div className="relative">
                  <Input
                    id={key}
                    type={showPasswords[visKey] ? 'text' : 'password'}
                    value={passwordForm[key]}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, [key]: e.target.value }))}
                    required
                    className="pr-10"
                    data-testid={`password-input-${key}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, [visKey]: !prev[visKey] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid={`toggle-visibility-${key}`}
                  >
                    {showPasswords[visKey] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            ))}
            {passwordForm.new_password && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
              <p className="text-sm text-red-500" data-testid="password-mismatch-error">Passwords do not match</p>
            )}
            <Button
              type="submit"
              disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
              className="w-full"
              data-testid="change-password-submit-btn"
            >
              {changingPassword ? 'Changing...' : 'Update Password'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'time-off', label: 'Time Off', icon: Calendar },
    { id: 'onboarding', label: 'Onboarding', icon: ClipboardList },
    { id: 'benefits', label: 'Benefits', icon: Heart },
    { id: 'integrations', label: 'Integrations', icon: Plug },
    { id: 'security', label: 'Security', icon: Shield },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="my-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
          <User size={28} className="text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{person.first_name} {person.last_name}</h1>
          <p className="text-muted-foreground">{person.job_title || person.discipline || 'Team Member'} {person.department ? `- ${person.department}` : ''}</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={person.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{person.status}</Badge>
            {person.employment_status && <Badge className="bg-blue-100 text-blue-800">{person.employment_status}</Badge>}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`my-profile-tab-${tab.id}`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Contact Info */}
          <Card className="p-6" data-testid="my-contact-info">
            <h3 className="font-medium mb-4 flex items-center gap-2"><Mail size={16} /> Contact Information</h3>
            <div className="space-y-3">
              {person.work_email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail size={14} className="text-muted-foreground" />
                  <span>{person.work_email}</span>
                </div>
              )}
              {person.email && person.email !== person.work_email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail size={14} className="text-muted-foreground" />
                  <span>{person.email} (personal)</span>
                </div>
              )}
              {(person.cell_phone || person.phone) && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone size={14} className="text-muted-foreground" />
                  <span>{person.cell_phone || person.phone}</span>
                </div>
              )}
              {person.state && (
                <div className="flex items-center gap-3 text-sm">
                  <MapPin size={14} className="text-muted-foreground" />
                  <span>{[person.address, person.city, person.state, person.zip].filter(Boolean).join(', ') || person.state}</span>
                </div>
              )}
            </div>
          </Card>

          {/* Job Info */}
          <Card className="p-6" data-testid="my-job-info">
            <h3 className="font-medium mb-4 flex items-center gap-2"><Briefcase size={16} /> Job Information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><p className="text-xs text-muted-foreground">Division</p><p className="text-sm font-medium">{person.division || '-'}</p></div>
              <div><p className="text-xs text-muted-foreground">Discipline</p><p className="text-sm font-medium">{person.discipline || '-'}</p></div>
              <div><p className="text-xs text-muted-foreground">Job Title</p><p className="text-sm font-medium">{person.job_title || '-'}</p></div>
              <div><p className="text-xs text-muted-foreground">Hire Date</p><p className="text-sm font-medium">{formatDate(person.hire_date)}</p></div>
              {person.worker_type && <div><p className="text-xs text-muted-foreground">Worker Type</p><p className="text-sm font-medium">{person.worker_type}</p></div>}
              {person.pay_type && <div><p className="text-xs text-muted-foreground">Pay Type</p><p className="text-sm font-medium">{person.pay_type}</p></div>}
            </div>
          </Card>

          {/* Quick Status Cards */}
          <Card className="p-6" data-testid="my-onboarding-status">
            <h3 className="font-medium mb-3 flex items-center gap-2"><ClipboardList size={16} /> Onboarding Status</h3>
            {onboardingData && onboardingData.checklist && onboardingData.checklist.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Badge className={onboardingData.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                    {onboardingData.status}
                  </Badge>
                  <span className="text-sm font-medium">{onboardingData.progress_percent}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${onboardingData.progress_percent}%` }} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{onboardingData.completed_items}/{onboardingData.total_items} items completed</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No onboarding items assigned</p>
            )}
          </Card>

          <Card className="p-6" data-testid="my-benefits-status">
            <h3 className="font-medium mb-3 flex items-center gap-2"><Heart size={16} /> Benefits</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Eligible</span>
                {person.benefits_eligible ? <CheckCircle size={16} className="text-green-600" /> : <XCircle size={16} className="text-gray-400" />}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Enrolled</span>
                {person.benefits_enrolled ? <CheckCircle size={16} className="text-green-600" /> : <XCircle size={16} className="text-gray-400" />}
              </div>
              {person.benefits_group && (
                <div><p className="text-xs text-muted-foreground">Group</p><p className="text-sm font-medium">{person.benefits_group}</p></div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Time Off Tab */}
      {activeTab === 'time-off' && (
        <PersonnelPTO personnelId={person.id} isAdmin={false} />
      )}

      {/* Onboarding Tab */}
      {activeTab === 'onboarding' && (
        <Card className="p-6" data-testid="my-onboarding-checklist">
          <input ref={fileInputRef} type="file" className="hidden" onChange={handleUploadFile}
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.bmp,.webp,.txt" data-testid="my-onboarding-file-input" />
          <h3 className="font-medium mb-4 flex items-center gap-2"><ClipboardList size={16} /> My Onboarding Checklist</h3>
          {onboardingData && onboardingData.checklist && onboardingData.checklist.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-2">
                <Badge className={onboardingData.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                  {onboardingData.status}
                </Badge>
                <span className="text-sm">{onboardingData.completed_items}/{onboardingData.total_items} items ({onboardingData.progress_percent}%)</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2.5 mb-4">
                <div className="bg-primary h-2.5 rounded-full transition-all" style={{ width: `${onboardingData.progress_percent}%` }} />
              </div>
              <div className="space-y-2">
                {onboardingData.checklist.map((item) => (
                  <div key={item.id} className={`flex items-start justify-between p-3 rounded-lg border ${item.status === 'completed' ? 'bg-green-50 border-green-200' : 'border-border'}`} data-testid={`my-onboarding-item-${item.id}`}>
                    <div className="flex items-start gap-3">
                      {(item.item_type === 'document' || item.item_type === 'signature') ? (
                        <span
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${item.status === 'completed' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-200 text-gray-300'}`}
                          title={item.item_type === 'document' ? 'Completes when you upload the file' : 'Completes when signed'}
                        >
                          {item.status === 'completed' && <CheckCircle size={12} />}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleUpdateOnboardingItem(item.id, item.status === 'completed' ? 'pending' : 'completed')}
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${item.status === 'completed' ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-primary'}`}
                        >
                          {item.status === 'completed' && <CheckCircle size={12} />}
                        </button>
                      )}
                      <div>
                        <p className={`text-sm font-medium ${item.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{item.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className="text-[10px] px-1.5 py-0 bg-muted text-muted-foreground">{item.item_type}</Badge>
                          {item.required && <Badge className="text-[10px] px-1.5 py-0 bg-red-50 text-red-600">Required</Badge>}
                          {item.item_type === 'document' && item.credential_type && (
                            <Badge className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700">{item.credential_type}</Badge>
                          )}
                          {item.due_at && item.status !== 'completed' && (
                            item.overdue
                              ? <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700" data-testid={`my-overdue-${item.id}`}>Overdue · {formatDate(item.due_at?.split('T')[0])}</Badge>
                              : <Badge className="text-[10px] px-1.5 py-0 bg-orange-50 text-orange-700">Due {formatDate(item.due_at?.split('T')[0])}</Badge>
                          )}
                        </div>
                        {item.description && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{item.description}</p>}
                        {item.attachment?.id && (
                          <a
                            href={`${API}/onboarding/attachments/${item.attachment.id}?token=${encodeURIComponent(localStorage.getItem('token') || '')}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                            data-testid={`my-onboarding-attachment-${item.id}`}
                          >
                            <Download size={12} /> {item.attachment.file_name || 'Reference file'}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.item_type === 'document' && item.status !== 'completed' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busyItem === item.id} onClick={() => triggerUpload(item)} data-testid={`my-upload-${item.id}`}>
                          <Upload size={12} className="mr-1" /> {busyItem === item.id ? 'Uploading…' : 'Upload'}
                        </Button>
                      )}
                      {item.item_type === 'signature' && item.esign_template_id && item.status !== 'completed' && (
                        <>
                          {item.esign_awaiting ? (
                            <span className="text-xs px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid={`my-awaiting-${item.id}`}>
                              Awaiting signature
                            </span>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 text-xs text-primary" disabled={busyItem === item.id} onClick={() => handleSignItem(item)} data-testid={`my-sign-${item.id}`}>
                              <PenLine size={12} className="mr-1" /> {busyItem === item.id ? 'Opening…' : (item.esign_request_id ? 'Open to sign' : 'Sign')}
                            </Button>
                          )}
                        </>
                      )}
                      {item.completed_at && (
                        <span className="text-xs text-muted-foreground">Completed {formatDate(item.completed_at?.split('T')[0])}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardList size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">No onboarding items assigned yet.</p>
            </div>
          )}
        </Card>
      )}

      {/* Benefits Tab */}
      {activeTab === 'benefits' && (
        <Card className="p-6" data-testid="my-benefits-detail">
          <h3 className="font-medium mb-4 flex items-center gap-2"><Heart size={16} /> Benefits Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Eligible</p>
              <p className="text-sm font-medium flex items-center gap-1">{person.benefits_eligible ? <><CheckCircle size={14} className="text-green-600" /> Yes</> : <><XCircle size={14} className="text-gray-400" /> No</>}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Enrolled</p>
              <p className="text-sm font-medium flex items-center gap-1">{person.benefits_enrolled ? <><CheckCircle size={14} className="text-green-600" /> Yes</> : <><XCircle size={14} className="text-gray-400" /> No</>}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Benefits Group</p>
              <p className="text-sm font-medium">{person.benefits_group || '-'}</p>
            </div>
            {person.benefits_charge_amount && (
              <div>
                <p className="text-xs text-muted-foreground">Charge Amount</p>
                <p className="text-sm font-medium">${parseFloat(person.benefits_charge_amount).toFixed(2)}</p>
              </div>
            )}
            {person.benefits_charge_frequency && (
              <div>
                <p className="text-xs text-muted-foreground">Charge Frequency</p>
                <p className="text-sm font-medium capitalize">{person.benefits_charge_frequency.replace('_', ' ')}</p>
              </div>
            )}
          </div>
          {person.benefits_notes && (
            <div className="mt-4 p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Notes</p>
              <p className="text-sm">{person.benefits_notes}</p>
            </div>
          )}
        </Card>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-6 max-w-3xl" data-testid="my-integrations">
          <MyGmailConnection />
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <Card className="p-6 max-w-lg" data-testid="my-security-settings">
          <h3 className="font-medium mb-6 flex items-center gap-2"><Lock size={16} /> Change Password</h3>
          <form onSubmit={handleChangePassword} className="space-y-4">
            {[
              { key: 'current_password', label: 'Current Password', visKey: 'current' },
              { key: 'new_password', label: 'New Password', visKey: 'new' },
              { key: 'confirm_password', label: 'Confirm New Password', visKey: 'confirm' },
            ].map(({ key, label, visKey }) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={key}>{label}</Label>
                <div className="relative">
                  <Input
                    id={key}
                    type={showPasswords[visKey] ? 'text' : 'password'}
                    value={passwordForm[key]}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, [key]: e.target.value }))}
                    required
                    className="pr-10"
                    data-testid={`password-input-${key}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, [visKey]: !prev[visKey] }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid={`toggle-visibility-${key}`}
                  >
                    {showPasswords[visKey] ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            ))}
            {passwordForm.new_password && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
              <p className="text-sm text-red-500" data-testid="password-mismatch-error">Passwords do not match</p>
            )}
            <Button
              type="submit"
              disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
              className="w-full"
              data-testid="change-password-submit-btn"
            >
              {changingPassword ? 'Changing...' : 'Update Password'}
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}
